// Full-screen waiting screen for MFA verification
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  LinearProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Slide,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmsIcon from '@mui/icons-material/Sms';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SpeedIcon from '@mui/icons-material/Speed';
import PeopleIcon from '@mui/icons-material/People';

export default function MFAWaitingScreen({ onClose }) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [dots, setDots] = useState('');
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const tips = [
    {
      icon: <LockIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Your Privacy Matters",
      text: "We only access your subscription list - never your messages or private content."
    },
    {
      icon: <VisibilityOffIcon sx={{ fontSize: 40, color: theme.palette.secondary.main }} />,
      title: "Temporary Authentication",
      text: "Your credentials are encrypted and only used for this verification. They're never stored permanently."
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />,
      title: "One-Time Process",
      text: "After verification, you'll have full access to creator reviews and ratings without logging in again."
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />,
      title: "Join Thousands of Users",
      text: "OnlyStats helps subscribers make informed decisions and avoid creators who don't deliver value."
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />,
      title: "Secure Connection",
      text: "All data is transmitted over encrypted connections. Your OnlyFans account remains completely secure."
    },
  ];

  // Animate dots for "Waiting" text
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Rotate tips every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex(prev => (prev + 1) % tips.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [tips.length]);

  // Smooth progress bar
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 0.5;
        return newProgress >= 95 ? 95 : newProgress; // Cap at 95% until actual completion
      });
    }, 500);
    return () => clearInterval(timer);
  }, []);

  // Progress through steps
  useEffect(() => {
    const timer1 = setTimeout(() => setActiveStep(1), 3000);
    const timer2 = setTimeout(() => setActiveStep(2), 8000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const steps = [
    {
      label: 'Connecting to OnlyFans',
      icon: <SecurityIcon />,
      description: 'Securely authenticating your account',
    },
    {
      label: 'Requesting Verification Code',
      icon: <PhoneAndroidIcon />,
      description: 'Sending SMS to your registered phone',
    },
    {
      label: 'Waiting for Code',
      icon: <SmsIcon />,
      description: 'Check your phone for the code',
    },
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: alpha(theme.palette.background.default, 0.98),
        backdropFilter: 'blur(10px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        overflowY: 'auto',
      }}
    >
      <Container maxWidth="md" sx={{ my: 'auto' }}>
        <Fade in timeout={500}>
          <Paper
            elevation={8}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            {/* Animated Icon */}
            <Zoom in timeout={700}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 3rem',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                      boxShadow: `0 8px 48px ${alpha(theme.palette.primary.main, 0.6)}`,
                    },
                  },
                }}
              >
                <PhoneAndroidIcon
                  sx={{
                    fontSize: 64,
                    color: 'white',
                    animation: 'shake 3s ease-in-out infinite',
                    '@keyframes shake': {
                      '0%, 100%': { transform: 'rotate(0deg)' },
                      '10%, 30%': { transform: 'rotate(-10deg)' },
                      '20%, 40%': { transform: 'rotate(10deg)' },
                      '50%': { transform: 'rotate(0deg)' },
                    },
                  }}
                />
              </Box>
            </Zoom>

            {/* Title */}
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              Hang Tight{dots}
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 3, fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              We're securely verifying your OnlyFans account
            </Typography>

            {/* Progress Bar with Percentage */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Verifying...
                </Typography>
                <Typography variant="body2" color="primary" fontWeight={600}>
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  },
                }}
              />
            </Box>

            {/* Estimated time */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 4, display: 'block' }}>
              This usually takes 1-2 minutes
            </Typography>

            {/* Stepper */}
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: index <= activeStep 
                            ? theme.palette.primary.main 
                            : alpha(theme.palette.primary.main, 0.2),
                          color: 'white',
                          transition: 'all 0.5s ease',
                          transform: index === activeStep ? 'scale(1.2)' : 'scale(1)',
                        }}
                      >
                        {index < activeStep ? (
                          <CheckCircleIcon sx={{ fontSize: 24 }} />
                        ) : (
                          React.cloneElement(step.icon, { sx: { fontSize: 24 } })
                        )}
                      </Box>
                    )}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: index <= activeStep ? 600 : 400,
                        color: index <= activeStep 
                          ? theme.palette.text.primary 
                          : theme.palette.text.disabled,
                      }}
                    >
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Rotating Tips Card */}
            <Box sx={{ minHeight: 200, position: 'relative', mb: 3 }}>
              {tips.map((tip, index) => (
                <Fade
                  key={index}
                  in={currentTipIndex === index}
                  timeout={1000}
                  style={{
                    position: currentTipIndex === index ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: currentTipIndex === index ? 'block' : 'none',
                  }}
                >
                  <Card
                    elevation={2}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      textAlign: 'center',
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      {tip.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                    >
                      {tip.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tip.text}
                    </Typography>
                  </Card>
                </Fade>
              ))}
            </Box>

            {/* Tip Navigation Dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
              {tips.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: currentTipIndex === index 
                      ? theme.palette.primary.main 
                      : alpha(theme.palette.primary.main, 0.3),
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>

            {/* Instructions */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
              }}
            >
              <Typography variant="body2" color="text.secondary" align="center" sx={{ fontWeight: 500 }}>
                📱 <strong>Check your phone!</strong> The code will arrive via SMS shortly.
                <br />
                ⏱️ Enter the code as soon as you receive it.
              </Typography>
            </Paper>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
}

